import { useEffect } from 'react';
import axios from 'axios';

import { useToast } from '../../hooks/toast';

/**
 * Registra no console erros que hoje somem sem deixar rastro.
 *
 * Deliberadamente NÃO renderiza a página de erro: uma promise rejeitada não
 * apaga a tela, e transformar isso em erro de página cheia seria regressão.
 * O toast só aparece fora de produção, para não expor ruído técnico.
 */
const GlobalErrorListener: React.FC = () => {
  const { addToast } = useToast();

  useEffect(() => {
    const isDevelopment = process.env.NODE_ENV !== 'production';

    const handleRejection = (event: PromiseRejectionEvent): void => {
      const { reason } = event;

      // Erros de API com resposta já são (ou deveriam ser) tratados no
      // call site; avisar de novo aqui só geraria spam.
      if (axios.isAxiosError(reason) && reason.response) {
        return;
      }

      // eslint-disable-next-line no-console
      console.error('[unhandledrejection]', reason);

      if (isDevelopment) {
        addToast({
          type: 'error',
          expression: 'Erro não tratado.',
          description: String(reason?.message ?? reason),
        });
      }
    };

    const handleError = (event: ErrorEvent): void => {
      // eslint-disable-next-line no-console
      console.error('[window.onerror]', event.error ?? event.message);
    };

    window.addEventListener('unhandledrejection', handleRejection);
    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('unhandledrejection', handleRejection);
      window.removeEventListener('error', handleError);
    };
  }, [addToast]);

  return null;
};

export default GlobalErrorListener;
